import { IncomingMessage, ServerResponse } from 'http';
import { type Response as ExpressResponse } from 'express';

import capitalize from './capitalize';

import { CookieClient } from '../types';

export interface CookieAdapter {
  get cookie(): string;
  set cookie(cookie: string);
}

export function generateCookieMap(cookieString: string): Map<string, string> {
  return new Map(
    cookieString.split(/; ?/).map(cookie => {
      const [key, value] = cookie.trim().split('=');

      return [key, decodeURI(value)];
    }) as Iterable<[string, string]>
  );
}

class ServerCookies {
  constructor(private request: IncomingMessage) {}

  getCookie(): string {
    const cookie = this.request.headers.cookie;
    if (!cookie) {
      throw new Error('Cookie header not found');
    }

    return cookie;
  }
}

export class ExpressCookieAdapter
  extends ServerCookies
  implements CookieAdapter
{
  constructor(
    request: IncomingMessage,
    private httpResponse: ExpressResponse
  ) {
    super(request);
  }

  get cookie() {
    return this.getCookie();
  }

  set cookie(cookie: string) {
    this.httpResponse.append('Set-Cookie', cookie);
  }
}

export class NodeCookieAdapter extends ServerCookies implements CookieAdapter {
  constructor(
    request: IncomingMessage,
    private httpResponse: ServerResponse
  ) {
    super(request);
  }

  get cookie() {
    return this.getCookie();
  }

  set cookie(cookie: string) {
    this.httpResponse.setHeader('Set-Cookie', cookie);
  }
}

export class ClientCookieAdapter implements CookieAdapter {
  constructor(private document: Document) {}

  get cookie(): string {
    return this.document.cookie;
  }

  set cookie(cookie: string) {
    this.document.cookie = cookie;
  }
}

export default class GeneralizedCookieClient implements CookieClient {
  constructor(private client: CookieAdapter) {}

  get(name: string): string | undefined {
    const cookie =
      this.client.cookie
        .split(/; ?/gim)
        .find(pairing => pairing.startsWith(name)) ?? '';

    const [, value] = cookie.split('=');

    return value ? decodeURI(value) : undefined;
  }

  getAll(): Map<string, string> {
    return generateCookieMap(this.client.cookie);
  }

  /**
   * The cookies are stored in one line string like:
   * "CVCurrentCity='Mesa'; CVCurrentState='AZ'; CVCurrentZip='85201'; MerchHasLoggedIn='true'; "
   */
  set(
    name: string,
    value: string,
    options: Record<string, string | number> = {}
  ): void {
    const cookieItems = [`${name}=${decodeURI(value)}`];

    Object.entries(options).forEach(([optionName, optionVal]) =>
      cookieItems.push(`${capitalize(optionName)}=${optionVal}`)
    );

    this.client.cookie = cookieItems.join('; ').trim();
  }
}
